import React, { useCallback } from 'react';
import { AsoScenarioScopeSourceResponse } from '@ydistri/api-sdk';
import { Card } from 'antd';
import { addToast } from '@ydistri/utils';
import { useUpdateAsoScopeSourceMutation } from '../apiAso';
import { useAsoScenarioStatus } from '../hooks/useAsoScenarioStatus';
import { Column } from '@ydistri/ds';
import { AsoValidationIssueMessages } from '../validation/AsoValidationIssueMessages';
import type { AsoValidationIssue } from '../validation/asoValidationTypes';
import { getRequestErrorMessage } from '../../../lib/utils/getRequestErrorMessage';
import { useAppDispatch } from '../../../store';
import AsoScopeAssignment from './AsoScopeAssignment';
import AsoSourceControls from './AsoSourceControls';

export interface AsoSourceScopeProps {
  asoScenarioId: number;
  scopeId: number;
  source: AsoScenarioScopeSourceResponse;
  validationIssues: readonly AsoValidationIssue[];
}

const AsoSourceScope: React.FC<AsoSourceScopeProps> = ({
  asoScenarioId,
  scopeId,
  source,
  validationIssues,
}) => {
  const [updateSource, { isLoading }] = useUpdateAsoScopeSourceMutation();
  const { isLocked } = useAsoScenarioStatus();
  const dispatch = useAppDispatch();

  const onChange = useCallback(
    async (field: 'isForced' | 'isForcedToZero', value: boolean) => {
      if (isLocked) return;

      try {
        await updateSource({
          asoScenarioId,
          scopeId,
          payload: { [field]: value },
        }).unwrap();
      } catch (error) {
        dispatch(
          addToast({
            isError: true,
            message: getRequestErrorMessage(error, 'The source settings could not be saved.'),
          }),
        );
      }
    },
    [asoScenarioId, dispatch, isLocked, scopeId, updateSource],
  );

  const onForcedChange = useCallback(
    (value: boolean) => void onChange('isForced', value),
    [onChange],
  );

  const onForcedToZeroChange = useCallback(
    (value: boolean) => void onChange('isForcedToZero', value),
    [onChange],
  );

  const onAssignScope = useCallback(
    async (selectedScopeId: number | null) => {
      await updateSource({
        asoScenarioId,
        scopeId,
        payload: { scopeId: selectedScopeId },
        previousReusableScopeId: source.scopeId,
      }).unwrap();
    },
    [asoScenarioId, scopeId, source.scopeId, updateSource],
  );

  return (
    <AsoScopeAssignment
      asoScenarioId={asoScenarioId}
      assignmentLabel="Source"
      isAssigning={isLoading}
      onAssignScope={onAssignScope}
      selectedScopeId={source.scopeId}
    >
      {({ content, selector }) => (
        <Card
          title="Source"
          extra={
            <AsoSourceControls
              isDisabled={isLoading || isLocked}
              isForced={source.isForced}
              isForcedToZero={source.isForcedToZero}
              scopeId={scopeId}
              onForcedChange={onForcedChange}
              onForcedToZeroChange={onForcedToZeroChange}
            />
          }
          type="inner"
        >
          <Column $gap="0.5rem">
            {selector}
            {content}
            <AsoValidationIssueMessages issues={validationIssues} />
          </Column>
        </Card>
      )}
    </AsoScopeAssignment>
  );
};

export default AsoSourceScope;
